import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import moment from 'moment';
import { StorageService } from 'ngx-webstorage-service';

import { PhxConstants } from '../model';
import { AppStorageService } from '../services/app-storage.service';
import { TokenRefreshService } from '../services/token-refresh.service';

import HTTPResponseStatus = PhxConstants.HTTPResponseStatus;

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private readonly endpointsToIgnoreAuthError: string[] = ['Account/Logout'];
  private readonly refreshTokenErrorKeys: string[] = ['invalid_grant', 'invalid_clientId'];

  constructor(
    private tokenRefreshService: TokenRefreshService,
    @Inject(AppStorageService) private storageService: StorageService
  ) {}

  private static addDefaultHeaders(request: HttpRequest<any>, token: string): HttpRequest<any> {
    if (token) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      });
    }

    return request;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const bearerToken = this.storageService.get('BearerToken');
    const bearerTokenExpiresOn = this.storageService.get('BearerTokenExpiresOn');

    const twoMinutesFromNow = moment().add(2, 'm');
    const getTokenCall = req.url.indexOf('token') !== -1;
    if (!getTokenCall && !this.tokenRefreshService.preemptiveSuppressed && bearerTokenExpiresOn && moment(bearerTokenExpiresOn) < twoMinutesFromNow) {
      return this.refreshAndRetry(req, next);
    }

    return next.handle(TokenInterceptor.addDefaultHeaders(req, bearerToken)).pipe(
      catchError(err => {
        if (err instanceof HttpErrorResponse) {
          if (err.status === HTTPResponseStatus.Unauthorized && !this.endpointsToIgnoreAuthError.some(endpoint => req.url.includes(endpoint))) {
            const refreshToken = this.storageService.get('RefreshToken');

            if (refreshToken && bearerToken) {
              this.tokenRefreshService.preemptiveSuppressed = false;
              return this.refreshAndRetry(req, next);
            }

            this.tokenRefreshService.logoutAndRedirect();
          }

          if (err.status === HTTPResponseStatus.BadRequest && err.error && this.refreshTokenErrorKeys.includes(err.error.error)) {
            this.tokenRefreshService.logoutAndRedirect();
          }
        }

        return throwError(() => err);
      })
    );
  }

  private refreshAndRetry(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.tokenRefreshService.ensureFreshToken().pipe(
      switchMap(token => next.handle(TokenInterceptor.addDefaultHeaders(request, token)))
    );
  }
}
